import { computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMediaStore } from '@/stores/mediaStore';
import { isViewingPatientChart } from '@/data/getters';
import { getSessionPatientPersonalId } from '@/utils/directivesSession';

/** The chart's way in: the media page on the open chart's patient (allData), or plain outside a chart. */
export function mediaLinkFor(allData) {
  const uid = isViewingPatientChart(allData) ? getSessionPatientPersonalId((allData && allData.patient) || {}) : '';
  return uid ? { path: '/patientMedia', query: { patient: uid } } : '/patientMedia';
}

/**
 * Where the person is inside the media experience. Every step lives in the URL, so the browser's
 * Back, a reload and a shared link all land on the same screen, whichever layout is chosen.
 *
 *   view=library                     the patient's media (default)
 *   view=set&set=ID                  a set in the viewer (with its progression, if any)
 *   view=item&item=ID&list=loose     one item, stepping through the patient's items in no set
 *   view=note&src=note:5521          the frozen attachments of a note or message
 *   view=compare&a=ID&b=ID           two items side by side
 *   view=edit&set=ID                 Edit set
 *   view=capture[&note=1]            capture → upload → tag (optionally as a note with photos)
 *
 * A step keeps the keys of the step it was opened from, and `prior` names that step: it labels the
 * back link and is where Back returns when there is no history entry to go back to (a reload or a
 * shared link). Layouts only decide where each step is shown; `on` wires the same events for all.
 */
const STEP_KEYS = ['view', 'set', 'item', 'list', 'src', 'at', 'a', 'b', 'note', 'prior'];
const VIEWER_STEPS = ['set', 'item', 'note'];
const LABELS = { library: 'Back to Media', set: 'Back to Set', item: 'Back to Media', note: 'Back to Note' };

export function useMediaNav() {
  const route = useRoute();
  const router = useRouter();
  const store = useMediaStore();

  const view = computed(() => route.query.view || 'library');
  const num = (k) => (route.query[k] ? Number(route.query[k]) : null);
  const base = (patient) => ({
    ...Object.fromEntries(Object.entries(route.query).filter(([k]) => !STEP_KEYS.includes(k))),
    ...(patient ? { patient } : {}),
  });
  const here = () => Object.fromEntries(Object.entries(route.query).filter(([k]) => STEP_KEYS.includes(k) && k !== 'prior'));

  // The step Back returns to: its own view, with the keys that view needs.
  const parent = computed(() => {
    const q = route.query;
    const prior = q.prior || (view.value === 'edit' ? 'set' : 'library');
    if (prior === 'set' && q.set) return { view: 'set', set: q.set };
    if (prior === 'item' && q.item) return { view: 'item', item: q.item, list: q.list };
    if (prior === 'note' && q.src) return { view: 'note', src: q.src };
    return { view: 'library' };
  });

  function push(step) {
    const clean = Object.fromEntries(Object.entries(step).filter(([, v]) => v !== undefined && v !== null));
    router.push({ query: { ...base(), ...here(), at: undefined, ...clean, prior: view.value } });
  }
  // After a delete, a move or an upload there is nothing to go back to: the step is replaced.
  function replaceWith(step, patient) {
    router.replace({ query: step.view === 'library' ? base(patient) : { ...base(patient), ...step } });
  }
  function back() {
    const target = parent.value;
    const prev = window.history.state && window.history.state.back;
    const resolved = prev ? router.resolve(prev) : null;
    // The entry must also have the same layout and patient: one saved before a layout switch or
    // another patient would bring those back along with the step.
    const same = resolved && resolved.path === route.path && (resolved.query.view || 'library') === target.view
      && (target.view !== 'set' || resolved.query.set === String(target.set))
      && resolved.query.v === route.query.v && resolved.query.patient === route.query.patient;
    if (same) router.back();
    else replaceWith(target);
  }

  const openSet = (id) => push({ view: 'set', set: id });
  const openItem = (id, list) => push({ view: 'item', item: id, list });
  const openNote = (src, at = 0) => push({ view: 'note', src, at });
  const compare = (a, b) => push({ view: 'compare', a, b });
  const editSet = (id) => push({ view: 'edit', set: id });
  const capture = (asNote) => push({ view: 'capture', note: asNote ? 1 : undefined });

  const nav = reactive({
    view,
    query: computed(() => route.query),
    isViewer: computed(() => VIEWER_STEPS.includes(view.value)),
    setId: computed(() => num('set')),
    pair: computed(() => [num('a'), num('b')].filter(Boolean)),
    asNote: computed(() => !!route.query.note),
    backLabel: computed(() => LABELS[parent.value.view] || 'Back'),
    viewerContext: computed(() => {
      const q = route.query;
      if (view.value === 'item') return { kind: 'item', itemId: num('item'), list: q.list };
      if (view.value === 'note') return { kind: 'note', src: q.src, at: Number(q.at) || 0 };
      return { kind: 'set', setId: num('set') };
    }),
    // The card the open step came from, to mark it in a list shown beside it.
    activeKey: computed(() => {
      if (view.value === 'item') return `i${route.query.item}`;
      if (view.value === 'note') return route.query.src;
      return route.query.set ? `s${route.query.set}` : '';
    }),
    library: () => replaceWith({ view: 'library' }),
    back,
    on: {
      library: { 'open-set': openSet, 'open-item': openItem, 'open-note': openNote, 'edit-set': editSet, capture, compare },
      viewer: {
        back, compare, 'edit-set': editSet, 'open-set': openSet,
        // The progression strip swapped the set: the URL follows without a new history entry.
        'set-shown': (id) => router.replace({ query: { ...route.query, set: id } }),
      },
      compare: { back },
      editor: {
        back,
        saved: back,
        deleted: () => replaceWith({ view: 'library' }),
        // The page follows the set to its new patient.
        moved: (uid) => replaceWith({ view: 'set', set: num('set') }, uid),
      },
      capture: {
        back,
        done: (out) => {
          const uid = store.patientUid || undefined;
          if (out.source) replaceWith({ view: 'note', src: `note:${out.source.source_id}` }, uid);
          else if (out.set) replaceWith({ view: 'set', set: out.set.id }, uid);
          else replaceWith({ view: 'library' }, uid);
        },
      },
    },
  });
  return nav;
}
